import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Calendar, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang } from "../i18n";
import { AddressInput } from "./AddressInput";

const today = () => new Date().toISOString().split("T")[0];

export const BookingWidget = ({ defaultPickupText = "" } = {}) => {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [locations, setLocations] = useState([]);
  const [pickupText, setPickupText] = useState(defaultPickupText);
  const [dropoffText, setDropoffText] = useState("");
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [date, setDate] = useState(today());
  const [time, setTime] = useState("12:00");

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!pickup || !dropoff) {
      toast.error(t("booking.err_select_address"));
      return;
    }
    sessionStorage.setItem("cabnow_prefill", JSON.stringify({ pickup, dropoff, date, time }));
    navigate("/reservation");
  };

  const fieldCls =
    "w-full appearance-none rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-navy outline-none transition-colors focus:border-azure focus:ring-2 focus:ring-azure/15";

  return (
    <form
      onSubmit={submit}
      data-testid="hero-booking-widget"
      className="rounded-3xl border border-white/60 bg-white/95 p-6 shadow-2xl shadow-navy/10 backdrop-blur-xl sm:p-8"
    >
      <p className="overline-label mb-1">{t("widget.overline")}</p>
      <h2 className="mb-6 font-serif text-2xl font-semibold text-navy">{t("widget.title")}</h2>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-navy/50">
            <MapPin size={13} className="text-azure" /> {t("widget.pickup")}
          </span>
          <AddressInput
            testid="hero-pickup-input"
            className={fieldCls}
            placeholder={t("booking.address_ph")}
            value={pickupText}
            onChange={(v) => { setPickupText(v); setPickup(null); }}
            onSelect={(p) => { setPickupText(p.label); setPickup(p); }}
            lang={lang}
            places={locations}
          />
        </label>

        <label className="block">
          <span className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-navy/50">
            <MapPin size={13} className="text-gold" /> {t("widget.dropoff")}
          </span>
          <AddressInput
            testid="hero-dropoff-input"
            className={fieldCls}
            placeholder={t("booking.address_ph")}
            value={dropoffText}
            onChange={(v) => { setDropoffText(v); setDropoff(null); }}
            onSelect={(p) => { setDropoffText(p.label); setDropoff(p); }}
            lang={lang}
            places={locations}
          />
        </label>

        <label className="block">
          <span className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-navy/50">
            <Calendar size={13} className="text-azure" /> {t("widget.date")}
          </span>
          <input data-testid="hero-date-input" type="date" min={today()} value={date} onChange={(e) => setDate(e.target.value)} className={fieldCls} />
        </label>

        <label className="block">
          <span className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-navy/50">
            <Clock size={13} className="text-azure" /> {t("widget.time")}
          </span>
          <input data-testid="hero-time-input" type="time" value={time} onChange={(e) => setTime(e.target.value)} className={fieldCls} />
        </label>
      </div>

      <button
        type="submit"
        data-testid="hero-submit-button"
        className="group mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-azure py-4 text-sm font-bold text-white shadow-lg shadow-azure/30 transition-[background-color,transform] hover:bg-azure-hover hover:-translate-y-0.5"
      >
        {t("widget.submit")}
        <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
      </button>

      <p className="mt-4 text-center text-xs text-navy/50">
        {t("widget.note")}
        <a href="/reservation?devis=1" data-testid="hero-quote-link" className="ml-1 font-semibold text-azure hover:underline">
          {t("widget.quote_link")}
        </a>
      </p>
    </form>
  );
};
